import * as React from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import Select from 'modules/core/components/Inputs/Select'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'

import type { RoundingDirection } from './form'
import type ToleranceSettingForm from './form'

export interface Props {
  form: ToleranceSettingForm
  onSubmit: () => void
  showEffectiveAtModal: () => void
}

const WorkedHours = ({ form, onSubmit, showEffectiveAtModal }: Props) => {
  const { has_dev_time_tracking_worked_time_rounding: workedTimeRoundingEnabled } = useFeatures([
    'dev_time_tracking_worked_time_rounding',
  ])
  const directionOptions = React.useMemo<{ label: string; value: RoundingDirection }[]>(
    () => [
      {
        label: i18n.t('time_tracking.settings.tolerance_settings.worked_hours.direction.up'),
        value: 'up',
      },
      {
        label: i18n.t('time_tracking.settings.tolerance_settings.worked_hours.direction.down'),
        value: 'down',
      },
      {
        label: i18n.t('time_tracking.settings.tolerance_settings.worked_hours.direction.nearest'),
        value: 'nearest',
      },
      {
        label: i18n.t(
          'time_tracking.settings.tolerance_settings.worked_hours.direction.no_rounding'
        ),
        value: 'no_rounding',
      },
      {
        label: i18n.t(
          'time_tracking.settings.tolerance_settings.worked_hours.direction.snap_to_planned'
        ),
        value: 'snap_to_planned',
      },
    ],
    []
  )
  const intervalOptions = React.useMemo<{ label: string; value: number }[]>(
    () => [
      {
        label: i18n.t('time_tracking.settings.tolerance_settings.worked_hours.interval.minutes_15'),
        value: 15,
      },
      {
        label: i18n.t('time_tracking.settings.tolerance_settings.worked_hours.interval.minutes_30'),
        value: 30,
      },
      {
        label: i18n.t('time_tracking.settings.tolerance_settings.worked_hours.interval.minutes_45'),
        value: 45,
      },
    ],
    []
  )

  const workedHoursEnabled =
    Boolean(form.get('workedHoursRoundingEnabled').value) ||
    Boolean(form.get('workedHoursToleranceEnabled').value)

  const onChange = () => {
    form.get('workedHoursToleranceEnabled').set(!form.get('workedHoursToleranceEnabled').value)

    !form.isNewRecord && disableWorkedHours() && onSubmit()
  }

  const disableWorkedHours = () => {
    return (
      !form.get('workedHoursRoundingEnabled').value &&
      !form.get('workedHoursToleranceEnabled').value
    )
  }

  const isDirty = form.workedHoursIsDirty

  return (
    <Box aria-label="attendance-tolerance-setting-form-worked-hours">
      <CollapsableCard
        open={workedHoursEnabled}
        Card={
          <Box gap="s8">
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="medium">
                {i18n.t('time_tracking.settings.tolerance_settings.worked_hours.title')}
              </Text.Normal>
              <TogglerCheckbox value={workedHoursEnabled} onChange={onChange} labelBefore />
            </Box>
            <Box paddingRight="s64">
              <Text.Normal color="grey800">
                {i18n.t('time_tracking.settings.tolerance_settings.worked_hours.description')}
              </Text.Normal>
            </Box>
          </Box>
        }
        Content={
          <Box gap="s16">
            <Box gap="s8">
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Text.Normal weight="medium">
                  {i18n.t('time_tracking.settings.tolerance_settings.worked_hours.below_section')}
                </Text.Normal>
                <InformationTooltip
                  information={i18n.t(
                    'time_tracking.settings.tolerance_settings.worked_hours.below_section_tooltip'
                  )}
                />
              </Box>
              <Box flexDirection="row" alignItems="center" width="full" gap="s12">
                {workedTimeRoundingEnabled && (
                  <Box width="full">
                    <Select
                      label={i18n.t(
                        'time_tracking.settings.tolerance_settings.worked_hours.rounding_method'
                      )}
                      field={form.get('roundingBelowDirection')}
                      options={directionOptions}
                    />
                  </Box>
                )}
                <Box width="full">
                  <Select
                    label={i18n.t(
                      'time_tracking.settings.tolerance_settings.worked_hours.rounding_intervals'
                    )}
                    field={form.get('belowEstimated')}
                    options={intervalOptions}
                  />
                </Box>
              </Box>
            </Box>
            <Box gap="s8">
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Text.Normal weight="medium">
                  {i18n.t('time_tracking.settings.tolerance_settings.worked_hours.above_section')}
                </Text.Normal>
                <InformationTooltip
                  information={i18n.t(
                    'time_tracking.settings.tolerance_settings.worked_hours.above_section_tooltip'
                  )}
                />
              </Box>
              <Box flexDirection="row" alignItems="center" width="full" gap="s12">
                {workedTimeRoundingEnabled && (
                  <Box width="full">
                    <Select
                      label={i18n.t(
                        'time_tracking.settings.tolerance_settings.worked_hours.rounding_method'
                      )}
                      field={form.get('roundingAboveDirection')}
                      options={directionOptions}
                    />
                  </Box>
                )}
                <Box width="full">
                  <Select
                    label={i18n.t(
                      'time_tracking.settings.tolerance_settings.worked_hours.rounding_intervals'
                    )}
                    field={form.get('aboveEstimated')}
                    options={intervalOptions}
                  />
                </Box>
              </Box>
            </Box>
            {isDirty && (
              <Box alignItems="flexEnd">
                <Button onClick={() => showEffectiveAtModal()}>{i18n.t('common.submit')}</Button>
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  )
}

export default observer(WorkedHours)
